
import {
  defineComponent, onMounted, ref, computed, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import ItemCase from '../components/items/ItemCase.vue';
import AppSkeletonCase from '@/components/skeletons/AppSkeletonCase.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import UsePagination from '@/composables/UsePagination';
import {
  ICase, IData, IList, IMixedData, IResponse,
} from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import DriveService from '../services/DriveService';
import { DIRECTION } from '@/helpers/isScrollEnd';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import AppNoMessage from '@/components/AppNoMessage.vue';

export default defineComponent({
  components: {
    ItemCase,
    AppSkeletonCase,
    RefreshPull,
    AppNoMessage,
  },
  setup() {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const router = useRouter();
    const route = useRoute();

    const dataLoading = ref(false);

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    const isNetwork = computed(() => route.meta.isNetwork);

    const cases = computed<ICase[]>(() => {
      if (driveStore.search) {
        return driveStore.searchCases;
      }
      return (isNetwork.value ? driveStore.networkCases.data : driveStore.cases.data);
    });

    const casesLastPage = computed(() => (isNetwork.value ? driveStore.networkCases.lastPage : driveStore.cases.lastPage));
    const casesCurrentPage = computed(() => (isNetwork.value ? driveStore.networkCases.page : driveStore.cases.page));

    const noCasesMessage = computed(() => {
      if (!dataLoading.value && !cases.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: isNetwork.value ? 'Nothing shared with you yet...' : 'No cases here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    function onCaseClick(iCase: ICase) {
      if (driveStore.isCaseSelector) {
        return;
      }

      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = iCase.photosAmount;
      driveStore.currentCase.filesAmount = iCase.filesAmount;

      if (isNetwork.value) {
        router.push(`/drive/case/network/${iCase.id}/view/`);
      } else {
        router.push(`/drive/case/my/${iCase.id}/view/`);
      }
    }

    async function fetchCacheData() {
      if (isNetwork.value) {
        return driveStore.fetchNetworkCases();
      }
      return driveStore.fetchCases();
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = async () => {
        if (isNetwork.value) {
          return DriveService.getNetworkCases(pageIdx.value, driveStore.search)
            .then((res) => {
              const casesResponse: any = { ...res };
              casesResponse.results.data.items = casesResponse.results.data.items.map((item: IMixedData) => ({ ...item.case as ICase, sharedTime: item.shared }));
              return casesResponse as IResponse<IData<IList<ICase>>>;
            });
        }
        return DriveService.getCases(pageIdx.value, driveStore.search);
      };

      return resolveRequest().then((response) => {
        if (currentSearch !== driveStore.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (pageIdx.value === 0) {
          driveStore.searchCases = response.results.data.items;
        } else {
          driveStore.searchCases.push(...response.results.data.items);
        }
        return response;
      });
    }

    function refreshCases() {
      if (isNetwork.value) {
        driveStore.networkCases.page = 0;
        driveStore.networkCases.lastPage = -1;
      } else {
        driveStore.cases.page = 0;
        driveStore.cases.lastPage = -1;
      }

      return fetchCacheData();
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshCases);

    function handleScroll() {
      if (loadingPull.value) {
        return;
      }
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, casesLastPage.value, casesCurrentPage.value);
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 1000);
    });

    function deselectAll(items: ICase[]): ICase[] {
      return items.map((item) => ({
        ...item,
        selected: false,
      }));
    }

    watch(() => driveStore.isCaseSelector, (newVal, prevVal) => {
      if (prevVal && !newVal) {
        driveStore.cases.data = deselectAll(driveStore.cases.data);
        driveStore.searchCases = deselectAll(driveStore.searchCases);
      }
    });

    onMounted(() => {
      driveStore.search = '';
      MonitoringService.sendPageMountedSpeed();
    });

    return {
      cases,
      dataLoading,
      mainStore,
      driveStore,
      router,
      isPageLoading,
      scroller,
      noCasesMessage,
      isNetwork,
      handleScroll,
      onCaseClick,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
    };
  },
});
